import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { isEmpty, isNil } from 'lodash';
import PropTypes from 'prop-types';
import JoditComponent, { IJoditEditorProps, Jodit } from 'jodit-react';
// Import Store
import { useAppSelector } from 'store';
import { selectAuthAccessToken } from 'store/slices/auth';
import { selectUserAppearance } from 'store/slices/userData';
// Import Hooks
import useToaster from 'hooks/UseToaster';
// Import Enums
import { TemplateValueTypeEnum } from 'enums';
// Import services
import { replaceDomainFromEnvironmentURL } from 'services/http';
import { stripExecutableHtml } from 'services/html';
// Import Constants
import { l } from 'constants/common';
// Import Styles
import 'assets/scss/joditEditor.scss';

const JoditEditor = ({ initialValue, onBlur, onChange, updateInsertionHandler, onFocus }) => {
    const { t } = useTranslation();

    const { showToaster } = useToaster();
    const accessToken = useAppSelector(selectAuthAccessToken);
    const uiTheme = useAppSelector(selectUserAppearance);
    const baseUrl = replaceDomainFromEnvironmentURL(process.env.REACT_APP_CRM_GALLERY_URL);
    const imageUploadUrl = `${baseUrl}Save/Image`;
    const imagesListUrl = `${baseUrl}List/Image`;
    const joditRef = useRef<Jodit>(null);
    // Jodit cleans pasted content itself; the stored body is stripped here so a saved handler never reaches the DOM
    const initialValueRef = useRef(stripExecutableHtml(initialValue));

    const changeHandler = useCallback(
        (value) => {
            onChange(value);
        },
        [onChange],
    );

    useEffect(() => {
        if (!joditRef.current) return;

        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const editor = joditRef.current.component.currentPlace.editor;
        editor.addEventListener('focus', onFocus);

        return () => {
            editor.removeEventListener('focus', onFocus);
        };
    }, [onFocus, joditRef]);

    const config: IJoditEditorProps['config'] = useMemo(() => {
        return {
            i18n: {
                en: {
                    'Drop image': 'Drop image or click',
                    'or click': `&nbsp; ${t(l.ImageUploadRequirements)} &nbsp;`,
                },
            },
            readonly: false, // all options from https://xdsoft.net/jodit/docs/
            theme: uiTheme === 1 ? 'dark' : '',
            placeholder: t(l.TypeYourText),
            removeButtons: [
                'classSpan',
                'file',
                'video',
                'speechRecognize',
                'spellcheck',
                'print',
                'about',
                'copy',
                'paste',
            ],
            uploader: {
                url: imageUploadUrl,
                isSuccess: (resp) => {
                    const hasError = resp.HasError;
                    const { AlertType, AlertMessage } = resp;

                    if (hasError) {
                        showToaster(AlertType, AlertMessage);
                    }

                    return !hasError;
                },
                defaultHandlerSuccess: (successData) => {
                    if (!isEmpty(successData.files)) {
                        successData.files.forEach((fileName) => {
                            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                            // @ts-ignore
                            joditRef.current.component.selection.insertImage(fileName);
                        });
                    }
                },
                process: (resp) => ({
                    files: resp.Data.map((imgData) => {
                        return imgData.Url;
                    }),
                }),
                headers: {
                    Authentication: accessToken,
                },
            },
            height: 400,
            showCharsCounter: false,
            showWordsCounter: false,
            showXPathInStatusbar: false,
            image: {
                useImageEditor: false,
            },
            filebrowser: {
                permissionsPresets: {
                    allowFileRemove: false,
                    allowFileRename: false,
                    allowFolders: false,
                    allowFolderCreate: false,
                    allowFolderMove: false,
                    allowFolderRemove: false,
                    allowFolderRename: false,
                    allowImageResize: false,
                    allowImageCrop: false,
                },
                getMessage: (resp) => resp.message,
                isSuccess: (res) => !res.HasError,
                showFileName: true,
                showFoldersPanel: false,
                saveStateInStorage: true as false,
                ajax: {
                    url: imagesListUrl, // URL of the file browser endpoint
                    process: (res) => {
                        return {
                            success: true,
                            time: '2021-12-23 16:30:01',
                            data: {
                                sources: [
                                    {
                                        path: '',
                                        baseurl: '',
                                        files: res.Data.map((imgData) => {
                                            return {
                                                type: 'image',
                                                name: imgData.Url,
                                                size: '250kb',
                                                thumb: `${imgData.Url}?method=cover&width=150&height=150&`,
                                            };
                                        }),
                                        name: 'default',
                                    },
                                ],
                                code: 220,
                            },
                        };
                    },
                    method: 'GET',
                    headers: {
                        Authentication: accessToken,
                    },
                },
            },
        };
    }, [t, uiTheme, imageUploadUrl, accessToken, imagesListUrl, showToaster]);

    const insertTextAtCursor = (dynamicVariable) => {
        const { FieldName, TypeConfig, PreviewValue } = dynamicVariable;
        let textToInsert = `{${FieldName}}`;

        if (!isNil(TypeConfig?.ValueType) && TypeConfig?.ValueType === TemplateValueTypeEnum.Link) {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            const selectedText = joditRef.current.component.selection.sel.toString().trim();

            textToInsert = `<a href="{${FieldName}}">${selectedText || textToInsert}</a>`;
        }

        if (
            !isNil(TypeConfig?.ValueType) &&
            TypeConfig?.ValueType === TemplateValueTypeEnum.Image &&
            !isNil(PreviewValue)
        ) {
            textToInsert = `<img src="${PreviewValue}" alt="image" />`;
        }

        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        joditRef.current.component.selection.insertHTML(` ${textToInsert} `);
    };

    updateInsertionHandler(insertTextAtCursor);

    return (
        <JoditComponent
            ref={joditRef}
            value={initialValueRef.current}
            config={config}
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            tabIndex={0}
            onBlur={onBlur} // preferred to use ONLY this option to update the content for performance reasons
            onChange={changeHandler}
        />
    );
};

JoditEditor.propTypes = {
    initialValue: PropTypes.string.isRequired,
    onBlur: PropTypes.func.isRequired,
    updateInsertionHandler: PropTypes.func,
    onFocus: PropTypes.func,
    onChange: PropTypes.func.isRequired,
};

export default JoditEditor;
